import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';

/**
 * Configuración para un polígono
 */
export interface PolygonConfig {
  id?: string;
  points: [number, number][];
  color?: string;
  fillColor?: string;
  width?: number;
  opacity?: number;
  fillOpacity?: number;
  label?: string; // Texto a mostrar en el centro del polígono
}

/**
 * PolygonsLayerManager
 *
 * Manager especializado para renderizar polígonos en el mapa.
 *
 * Características:
 * - Múltiples polígonos simultáneos
 * - Configuración individual de colores de relleno y borde
 * - Opacidad configurable
 * - NO usa el patrón de EntityLayerManager (geometrías no son entidades puntuales)
 *
 * Uso típico:
 * - Zonas de clientes
 * - Áreas de cobertura
 * - Polígonos de geofencing
 */
@Injectable({
  providedIn: 'root',
})
export class PolygonsLayerManager {
  private map: mapboxgl.Map | null = null;
  private sourceId = 'polygons-source';
  private fillLayerId = 'polygons-fill-layer';
  private outlineLayerId = 'polygons-outline-layer';
  private labelLayerId = 'polygons-label-layer';
  private isInitialized = false;

  // ========== LIFECYCLE ==========

  initialize(map: mapboxgl.Map): void {
    if (this.isInitialized) {
      console.warn('⚠️ PolygonsLayerManager ya está inicializado');
      return;
    }

    this.map = map;
    this.createSourceAndLayers();
    this.isInitialized = true;
  }

  cleanup(): void {
    if (!this.map) return;

    // Remover layers
    if (this.map.getLayer(this.labelLayerId)) {
      this.map.removeLayer(this.labelLayerId);
    }

    if (this.map.getLayer(this.outlineLayerId)) {
      this.map.removeLayer(this.outlineLayerId);
    }

    if (this.map.getLayer(this.fillLayerId)) {
      this.map.removeLayer(this.fillLayerId);
    }

    // Remover source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    this.map = null;
    this.isInitialized = false;
  }

  // ========== CREACIÓN ==========

  private createSourceAndLayers(): void {
    if (!this.map) return;

    // Source para polígonos
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        buffer: 0, // OPTIMIZACIÓN: Sin buffer para mejor performance
        maxzoom: 16, // OPTIMIZACIÓN: Limitar zoom máximo
        tolerance: 0.375, // Default, puede incrementarse para simplificar geometrías
      });
    }

    // Layer de relleno
    if (!this.map.getLayer(this.fillLayerId)) {
      this.map.addLayer({
        id: this.fillLayerId,
        type: 'fill',
        source: this.sourceId,
        paint: {
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.2],
        },
      });
    }

    // Layer de borde (encima del relleno)
    if (!this.map.getLayer(this.outlineLayerId)) {
      this.map.addLayer({
        id: this.outlineLayerId,
        type: 'line',
        source: this.sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'width'],
          'line-opacity': ['coalesce', ['get', 'opacity'], 1],
        },
      });
    }

    // Layer de texto (etiqueta en el centro del polígono)
    if (!this.map.getLayer(this.labelLayerId)) {
      this.map.addLayer({
        id: this.labelLayerId,
        type: 'symbol',
        source: this.sourceId,
        filter: ['has', 'label'],
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-anchor': 'center',
          'text-offset': [0, 0],
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2,
        },
      });
    }
  }

  // ========== DATOS ==========

  /**
   * Actualiza los polígonos en el mapa
   */
  updatePolygons(polygons: PolygonConfig[]): void {
    if (!this.map || !this.isInitialized) {
      console.warn('⚠️ PolygonsLayerManager no está inicializado');
      return;
    }

    const features = polygons.map((polygon, index) =>
      this.polygonToFeature(polygon, index),
    );

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
      type: 'FeatureCollection',
      features,
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geojson);
    }
  }

  /**
   * Limpia todos los polígonos
   */
  clearPolygons(): void {
    if (!this.map || !this.isInitialized) return;

    const emptyGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
      type: 'FeatureCollection',
      features: [],
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(emptyGeoJSON);
    }
  }

  // ========== CONVERSIÓN ==========

  private polygonToFeature(
    polygon: PolygonConfig,
    index: number,
  ): GeoJSON.Feature<GeoJSON.Polygon> {
    // Cerrar el polígono si no está cerrado
    const coordinates = [...polygon.points];
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];

    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push([...first]);
    }

    const properties: Record<string, unknown> = {
      color: polygon.color || '#3bb2d0',
      fillColor: polygon.fillColor || polygon.color || '#3bb2d0',
      width: polygon.width || 2,
      opacity: polygon.opacity !== undefined ? polygon.opacity : 1,
      fillOpacity:
        polygon.fillOpacity !== undefined ? polygon.fillOpacity : 0.2,
    };

    // Solo agregar la etiqueta si tiene contenido (el layer filtra por ['has','label'])
    if (polygon.label) {
      properties['label'] = polygon.label;
    }

    return {
      type: 'Feature',
      id: polygon.id || `polygon-${index}`,
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates], // Array de rings (exterior + huecos)
      },
      properties,
    };
  }

  // ========== VISIBILIDAD ==========

  setVisibility(visible: boolean): void {
    if (!this.map) return;

    const visibility = visible ? 'visible' : 'none';

    if (this.map.getLayer(this.fillLayerId)) {
      this.map.setLayoutProperty(this.fillLayerId, 'visibility', visibility);
    }

    if (this.map.getLayer(this.outlineLayerId)) {
      this.map.setLayoutProperty(this.outlineLayerId, 'visibility', visibility);
    }

    if (this.map.getLayer(this.labelLayerId)) {
      this.map.setLayoutProperty(this.labelLayerId, 'visibility', visibility);
    }
  }

  getVisibility(): boolean {
    if (!this.map || !this.map.getLayer(this.fillLayerId)) return false;
    return (
      this.map.getLayoutProperty(this.fillLayerId, 'visibility') === 'visible'
    );
  }
}
